import { useQueryClient } from '@tanstack/react-query';
import uniq from 'lodash.uniq';
import { useEffect, useMemo, useState } from 'react';

import { type ConversationMessage } from '@mai/types';

import { AGENT_RUNS_QUERY_KEY, useAgentRunsQuery } from '@queries/agent-runs';
import { useConversationAgentRunsQuery } from '@queries/conversation-agent-runs';
import {
  CONVERSATION_MESSAGES_QUERY_KEY,
  useConversationMessagesQuery,
} from '@queries/conversation-messages';
import { CONVERSATIONS_QUERY_KEY } from '@queries/conversations';
import { useFileRevisionsQuery } from '@queries/file-revisions';
import { useTextChunksQuery } from '@queries/text-chunks';
import { useTextContentsQuery } from '@queries/text-contents';
import { useWebsocketPubSub } from '@queries/websockets';

type ConversationMessageWithStreamingSupported = Pick<
  ConversationMessage,
  | 'conversationMessageId'
  | 'role'
  | 'content'
  | 'thoughts'
  | 'createdAt'
  | 'tasks'
  | 'requestedModel'
> & {
  isStreaming: boolean;
};

export const useConversationMessages = ({
  conversationId,
}: {
  conversationId: string;
}): {
  conversationMessages: ConversationMessageWithStreamingSupported[];
  isLoading: boolean;
  isFetching: boolean;
  isStreaming: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
} => {
  const { subscribe, unsubscribe } = useWebsocketPubSub();
  const queryClient = useQueryClient();

  const conversationMessagesQuery = useConversationMessagesQuery(
    {
      conversationIds: [conversationId],
    },
    !!conversationId,
  );
  const conversationMessagesData = useMemo(
    () => conversationMessagesQuery.data ?? [],
    [conversationMessagesQuery.data],
  );

  const [streamingMessage, setStreamingMessage] = useState<{
    conversationMessageId: string;
    message: string;
    thoughts: { text: string; createdAt: Date }[];
  } | null>(null);

  // Subscribe to conversation message created events
  // When an event has been received here, it indicates that streaming has completed
  useEffect(() => {
    if (!conversationId) {
      return;
    }
    const subscriptionId = subscribe(
      {
        topic: 'resource_updated',
        options: {
          resourceType: 'conversation',
          resourceId: conversationId,
        },
      },
      (event) => {
        if (event.type !== 'resource_updated') {
          return;
        }
        if (event.data.resourceType !== 'conversation') {
          return;
        }
        if (event.data.resourceId !== conversationId) {
          return;
        }
        void queryClient.invalidateQueries({
          queryKey: [CONVERSATIONS_QUERY_KEY],
        });
        void queryClient.invalidateQueries({
          queryKey: [CONVERSATION_MESSAGES_QUERY_KEY],
        });
        void queryClient.invalidateQueries({
          queryKey: [AGENT_RUNS_QUERY_KEY],
        });
      },
    );
    return () => {
      unsubscribe(subscriptionId);
    };
    // NOTE: do not include subscribe/unsubscribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Subscribe to conversation message stream updated events
  // When an event has been received here, it indicates that streaming is in progress
  useEffect(() => {
    if (!conversationId) {
      return;
    }
    const subscriptionId = subscribe(
      {
        topic: 'conversation_message_stream_updated',
        options: {
          conversationId,
        },
      },
      (event) => {
        if (
          event.type !== 'conversation_message_stream_updated' ||
          event.data.conversationId !== conversationId ||
          conversationMessagesData.some(
            (m) => m.conversationMessageId === event.data.conversationMessageId,
          )
        ) {
          return;
        }
        setStreamingMessage({
          conversationMessageId: event.data.conversationMessageId,
          message: event.data.message,
          thoughts: event.data.thoughts.map((thought) => ({
            text: thought.text,
            createdAt: new Date(thought.createdAt),
          })),
        });
      },
    );
    return () => {
      unsubscribe(subscriptionId);
    };
  }, [conversationId, conversationMessagesData, subscribe, unsubscribe]);

  const isStreaming = useMemo(
    () =>
      Boolean(
        streamingMessage &&
          !conversationMessagesData.some(
            (m) =>
              m.conversationMessageId ===
              streamingMessage.conversationMessageId,
          ),
      ),
    [conversationMessagesData, streamingMessage],
  );

  const messagesWithStreamingStatus: ConversationMessageWithStreamingSupported[] =
    useMemo(() => {
      const messages: ConversationMessageWithStreamingSupported[] =
        conversationMessagesData.map((m) => ({
          ...m,
          isStreaming: false,
          thoughts: [],
        }));

      // Clear streaming state if the message exists in our history
      if (
        streamingMessage &&
        messages.some(
          (m) =>
            m.conversationMessageId === streamingMessage.conversationMessageId,
        )
      ) {
        setStreamingMessage(null);
        return messages;
      }

      if (streamingMessage && isStreaming) {
        messages.push({
          conversationMessageId: streamingMessage.conversationMessageId,
          role: 'assistant',
          content: { text: streamingMessage.message },
          thoughts: streamingMessage.thoughts,
          createdAt: new Date(),
          isStreaming: true,
          tasks: [],
        });
      }
      return messages.sort(
        (a, b) => a.createdAt.getTime() - b.createdAt.getTime(),
      );
    }, [conversationMessagesData, streamingMessage, isStreaming]);

  const refetch = async (): Promise<void> => {
    await conversationMessagesQuery.refetch();
  };

  return {
    conversationMessages: messagesWithStreamingStatus,
    isLoading: conversationMessagesQuery.isLoading,
    isFetching: conversationMessagesQuery.isFetching,
    error: conversationMessagesQuery.error,
    isStreaming,
    refetch,
  };
};

export const useConversationAgentRuns = (conversationId: string) => {
  // Fetch agent runs associated with this conversation
  const {
    data: conversationAgentRuns = [],
    isLoading: isLoadingAgentRuns,
    refetch: refetchConversationAgentRuns,
  } = useConversationAgentRunsQuery(
    { conversationIds: [conversationId] },
    !!conversationId,
  );

  // Get the agent run IDs from the conversation-agent-runs
  const agentRunIds = conversationAgentRuns.map((car) => car.agentRunId);

  // Fetch the actual agent runs data
  const {
    data: agentRuns = [],
    isLoading: isLoadingAgentRunDetails,
    refetch: refetchAgentRuns,
  } = useAgentRunsQuery(
    { agentRunIds, agentTypes: ['general'] },
    agentRunIds.length > 0,
  );

  // Transform agent runs to include task and status information
  const transformedAgentRuns = agentRuns.map((run) => {
    // Extract task name from agentInput if available
    const task = run.agentInput?.task || run.agentInput?.name || null;

    // Extract status from agentOutput if available
    // Consider a task completed if it has any output, regardless of status field
    const status = run.agentOutput ? 'completed' : 'in_progress';

    return {
      ...run,
      task,
      status,
    };
  });

  const refetch = async () => {
    await Promise.all([refetchConversationAgentRuns(), refetchAgentRuns()]);
  };

  return {
    agentRuns: transformedAgentRuns,
    isLoading: isLoadingAgentRuns || isLoadingAgentRunDetails,
    refetch,
  };
};

/**
 * Pre process the content and find all inline citations, source_<nanoid>
 * Returns a list in order of first appearance in the content
 */
function getSourceIdsFromContent(content: string) {
  const sourceIdRegex = /\[source_(.*?)\]/g;
  return uniq(
    Array.from(content.matchAll(sourceIdRegex)).flatMap((match) => {
      const captureGroup = match[1];
      if (!captureGroup) {
        return [];
      }
      return [captureGroup];
    }),
  ).sort((a, b) => {
    const aIndex = content.indexOf(`[source_${a}]`);
    const bIndex = content.indexOf(`[source_${b}]`);
    return aIndex - bIndex;
  });
}

/**
 * Pre process the content and find all inline citations, source_<nanoid>
 * Returns a list in order of first appearance in the content
 */
function getChunkIdsFromContent(content: string) {
  const chunkIdRegex = /\[chunk_(.*?)\]/g;
  return uniq(
    Array.from(content.matchAll(chunkIdRegex)).flatMap((match) => {
      const captureGroup = match[1];
      if (!captureGroup) {
        return [];
      }
      return [captureGroup];
    }),
  );
}

export const useConversationSourceIds = (conversationId: string) => {
  const { conversationMessages } = useConversationMessages({
    conversationId,
  });

  const sourceIds = useMemo(() => {
    return conversationMessages.flatMap((message) => {
      return getSourceIdsFromContent(message.content.text);
    });
  }, [conversationMessages]);

  const chunkIds = useMemo(() => {
    return conversationMessages.flatMap((message) => {
      return getChunkIdsFromContent(message.content.text);
    });
  }, [conversationMessages]);

  const textChunksQuery = useTextChunksQuery(
    {
      textChunkIds: chunkIds,
      pageSize: chunkIds.length,
    },
    !!chunkIds.length,
  );

  // Fetch text contents
  const textContentsQuery = useTextContentsQuery(
    {
      textContentIds: textChunksQuery.data?.map((chunk) => chunk.textContentId),
      pageSize: textChunksQuery.data?.length,
    },
    !!textChunksQuery.data?.length,
  );

  // Fetch file revisions
  const fileRevisionsQuery = useFileRevisionsQuery(
    {
      fileRevisionIds: textContentsQuery.data?.map(
        (content) => content.fileRevisionId,
      ),
      pageSize: textContentsQuery.data?.length,
    },
    !!textContentsQuery.data?.length,
  );

  // Fetch source data
  const allSourceIds = uniq([
    ...sourceIds,
    ...(fileRevisionsQuery.data?.map((revision) => revision.sourceId) ?? []),
  ]);

  return allSourceIds;
};
